import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { PuffLoader } from 'react-spinners';
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from '@headlessui/react';
import { CameraIcon } from '@heroicons/react/24/outline';

const ActiveEventItem = ({ event }) => {
  if (!event) return null;

  const formatDateRange = (date) => {
    if (!date || !date.type) return '';

    if (
      date.type === 'fixed' &&
      date.startDate &&
      date.endDate &&
      moment(date.startDate).isValid() &&
      moment(date.endDate).isValid()
    ) {
      const fromFormatted = moment(date.startDate).format('MMM D, YYYY');
      const toFormatted = moment(date.endDate).format('MMM D, YYYY');
      return `${fromFormatted} - ${toFormatted}`;
    }

    if (
      date.type === 'segmented' &&
      Array.isArray(date.days) &&
      date.days.length > 0
    ) {
      const sortedDays = [...date.days].sort((a, b) =>
        moment(a.date).diff(moment(b.date))
      );
      const start = moment(sortedDays[0].date).format('MMM D, YYYY');
      const end = moment(sortedDays[sortedDays.length - 1].date).format(
        'MMM D, YYYY'
      );
      return `${start} - ${end}`;
    }

    return '';
  };

  return (
    <div className="flex items-center gap-3 cursor-pointer bg-gray-100 rounded-md border p-2 hover:bg-yellow-05/20 transition-colors">
      <div className="h-12 w-12 border rounded-md flex-shrink-0 flex items-center justify-center overflow-hidden">
        {event.avatar ? (
          <img
            src={event.avatar}
            alt={event.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <CameraIcon className="text-gray-500 h-6 w-6" />
        )}
      </div>
      <div className="flex flex-col justify-center min-w-0">
        <h4 className="font-semibold text-sm text-gray-800 truncate">
          {event.name}
        </h4>
        <p className="text-xs text-gray-500 truncate">{event.location}</p>
        <p className="text-xs text-yellow-05 font-medium">
          {formatDateRange(event.date)}
        </p>
      </div>
    </div>
  );
};

const ActiveEventsDropdown = ({ activeEvents, loading, onFetch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = () => {
    if (!loading && onFetch) {
      onFetch();
    }
  };

  return (
    <Menu as="div" className="relative text-left">
      <MenuButton
        onClick={handleClick}
        className="inline-flex justify-center text-gray-600 text-sm underline font-medium focus:outline-none focus:ring-2 hover:text-yellow-05 focus:ring-offset-2"
      >
        {loading ? (
          <PuffLoader color="#D97706" size={20} />
        ) : (
          t('event_detail.see_more_events')
        )}
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 p-2 space-y-2">
          {!loading && activeEvents.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              No active events
            </div>
          )}
          {activeEvents.map((ev) => (
            <MenuItem key={ev.id}>
              {({ active }) => (
                <div
                  className={`cursor-pointer ${active ? 'bg-yellow-100 rounded-md' : ''}`}
                  onClick={() => navigate(`/event/${ev.slug}`)}
                >
                  <ActiveEventItem event={ev} />
                </div>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
};

export default ActiveEventsDropdown;
